import { NavLink, Outlet, useLocation, Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { usePrintQueueWorker } from "@/lib/printQueueWorker";
import PrintHealthWidget from "@/components/PrintHealthWidget";
import OnlineOrderPopupWatcher from "@/components/OnlineOrderPopupWatcher";
import { KioskStaffAlerts } from "@/components/kiosk/KioskStaffAlerts";
import { HelpAlerts } from "@/components/kiosk/HelpAlerts";
import { KioskReturnBanner } from "@/components/kiosk/KioskReturnBanner";
import {
  House, ShoppingCart, Clock, Receipt, MonitorPlay, Truck,
  ForkKnife, Sliders, BookOpen, Package, Upload, Microphone,
  Users, Star, ChartBar, Gear, SignOut, Storefront, Warning, Bank, Ticket,
  CaretDoubleLeft, CaretDoubleRight, Lightning, Calendar, Megaphone,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, roles: ["owner", "manager"] },
  { to: "/pos", label: "POS Register", icon: ShoppingCart, roles: ["owner", "manager", "staff"] },
  { to: "/held", label: "Held Orders", icon: Clock, roles: ["owner", "manager", "staff"] },
  { to: "/orders", label: "Orders", icon: Receipt, roles: ["owner", "manager", "staff"] },
  { to: "/till", label: "Till", icon: Bank, roles: ["owner", "manager", "staff"] },
  { to: "/kds", label: "KDS", icon: MonitorPlay, roles: ["owner", "manager", "staff", "kitchen"] },
  { to: "/delivery", label: "Delivery", icon: Truck, roles: ["owner", "manager", "staff", "delivery_driver"] },
  { to: "/bookings", label: "Bookings", icon: Calendar, roles: ["owner", "manager", "staff"] },
  { to: "/device", label: "Device mode", icon: Storefront, roles: ["owner", "manager", "staff"] },
  { divider: "Menu", roles: ["owner", "manager"] },
  { to: "/menu", label: "Menu Builder", icon: ForkKnife, roles: ["owner", "manager"] },
  { to: "/modifiers", label: "Modifier Groups", icon: Sliders, roles: ["owner", "manager"] },
  { to: "/recipes", label: "Recipes", icon: BookOpen, roles: ["owner", "manager"] },
  { divider: "Stock", roles: ["owner", "manager"] },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["owner", "manager"] },
  { to: "/packaging", label: "Packaging", icon: Package, roles: ["owner", "manager"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["owner", "manager"] },
  { to: "/invoice", label: "Invoice Upload", icon: Upload, roles: ["owner", "manager"] },
  { to: "/stocktake", label: "Stocktake", icon: Microphone, roles: ["owner", "manager"] },
  { divider: "Growth", roles: ["owner", "manager"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["owner", "manager"] },
  { to: "/consignors", label: "Consignors", icon: Users, roles: ["owner", "manager"] },
  { to: "/drivers", label: "Driver Payouts", icon: Users, roles: ["owner", "manager"] },
  { to: "/loyalty", label: "Loyalty", icon: Star, roles: ["owner", "manager"] },
  { to: "/vouchers", label: "Vouchers", icon: Ticket, roles: ["owner", "manager"] },
  { to: "/overheads", label: "Overheads", icon: Bank, roles: ["owner", "manager"] },
  { to: "/profit-insights", label: "Profit Insights", icon: Lightning, roles: ["owner", "manager"] },
  { to: "/cashflow", label: "Cashflow", icon: Calendar, roles: ["owner", "manager"] },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["owner", "manager"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["owner", "manager"] },
];

export default function Layout() {
  const { business, venue, user, logout, subscriptionWarning, subscriptionStatus } = useAuth();
  // iter_101 (2026-06-07) P0 HOTFIX — Print-queue worker MOVED from POS.jsx
  // to the authenticated Layout so it polls /print/jobs/pending on EVERY
  // app page (POS, KDS, Orders, Settings, etc.) for as long as the
  // operator stays logged in on this tab. Non-printer devices NO-OP via
  // the printBridge `skipped:true` contract (see printBridge.js + the
  // worker's `result?.skipped` short-circuit), so this is safe to run
  // universally. Printer terminals continue to print exactly as before
  // — they just no longer go silent the moment staff browse to KDS or
  // Orders.
  usePrintQueueWorker({ venue, enabled: !!user });
  const loc = useLocation();
  const role = user?.role || "owner";
  // Sidebar collapse toggle — persists per-device via localStorage.
  // Default expanded. Collapsed state hides labels + dividers + brand text
  // and shows icon-only nav with `title` tooltips. Mobile layout untouched.
  const SIDEBAR_KEY = "mm_sidebar_collapsed";
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);
  // Defence-in-depth: delivery_driver is only allowed on /delivery (backend
  // also 403s every other route). Bounce them back if they manage to land
  // anywhere else.
  if (role === "delivery_driver" && loc.pathname !== "/delivery") {
    return <Navigate to="/delivery" replace />;
  }
  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(role));
  const showBanner = subscriptionWarning && (role === "owner" || role === "manager") && !loc.pathname.startsWith("/billing/return");
  const bannerLabel = ({
    past_due: "Your subscription payment is past due.",
    unpaid: "Your subscription is unpaid.",
    cancelled: "Your subscription is cancelled.",
    incomplete_expired: "Your subscription setup expired.",
  })[subscriptionStatus] || "Action required on your subscription.";
  // Hide chrome on POS for tablet focus mode? keep sidebar but collapse on mobile
  return (
    <div className="min-h-screen flex bg-[var(--sand-50)]">
      <aside className={`hidden md:flex flex-col bg-white border-r border-[rgba(28,25,23,0.08)] sticky top-0 h-screen transition-[width] duration-200 ease-out ${collapsed ? "w-16" : "w-60"}`} data-testid="sidebar" data-collapsed={collapsed ? "1" : "0"}>
        <div className={`py-5 border-b border-[rgba(28,25,23,0.06)] ${collapsed ? "px-3" : "px-5"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2"}`}>
            <div className="w-8 h-8 rounded-md bg-[var(--terracotta)] flex items-center justify-center shrink-0">
              <Storefront size={18} color="#fff" weight="fill" />
            </div>
            {!collapsed && (
              <div>
                <div className="font-heading font-semibold text-[15px] leading-tight">Margin Mate</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-500)]">One POS</div>
              </div>
            )}
          </div>
        </div>
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`flex items-center gap-2 mx-2 mt-2 mb-1 h-8 rounded-md text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--sand-100)] text-[11px] uppercase tracking-[0.18em] font-semibold ${collapsed ? "justify-center px-0" : "px-3"}`}
        >
          {collapsed ? <CaretDoubleRight size={14} /> : <><CaretDoubleLeft size={14} /> Collapse</>}
        </button>
        <nav className="flex-1 overflow-y-auto py-1 px-2 no-scrollbar">
          {visibleNav.map((n, i) =>
            n.divider ? (
              collapsed ? (
                <div key={i} className="border-t border-[rgba(28,25,23,0.06)] my-2 mx-1" aria-hidden="true" />
              ) : (
                <div key={i} className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-[var(--ink-500)] font-semibold">
                  {n.divider}
                </div>
              )
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                title={collapsed ? n.label : undefined}
                className={({ isActive }) =>
                  `flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2 my-0.5 rounded-md text-[13.5px] transition-all duration-150 ${
                    isActive
                      ? "bg-[var(--ink-900)] text-[var(--sand-50)]"
                      : "text-[var(--ink-700)] hover:bg-[var(--sand-100)]"
                  }`
                }
              >
                <n.icon size={17} weight={loc.pathname === n.to ? "fill" : "regular"} />
                {!collapsed && n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="border-t border-[rgba(28,25,23,0.06)] p-3">
          {!collapsed && (
            <div className="px-2 py-2">
              <div className="text-[13px] font-medium truncate">{business?.name}</div>
              <div className="text-[11px] text-[var(--ink-500)] truncate">{venue?.name} · {user?.name}</div>
            </div>
          )}
          {/* iter_129 — Print Health Widget. Compact icon-only when sidebar
              is collapsed; full row when expanded. Always visible. */}
          {user && role !== "delivery_driver" && (
            <div className={`mb-2 ${collapsed ? "flex justify-center" : ""}`}>
              <PrintHealthWidget venueId={venue?.id} compact={collapsed} />
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-button"
            title={collapsed ? `Logout — ${user?.name || ""}` : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center px-0" : "gap-2 px-3"} py-2 rounded-md text-[13px] text-[var(--ink-700)] hover:bg-[var(--sand-100)] transition-all`}
          >
            <SignOut size={16} /> {!collapsed && "Logout"}
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-[rgba(28,25,23,0.08)] z-40 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[var(--terracotta)] flex items-center justify-center">
            <Storefront size={14} color="#fff" weight="fill" />
          </div>
          <div className="font-heading font-semibold text-sm">Margin Mate</div>
        </div>
        <div className="flex items-center gap-2">
          {user && role !== "delivery_driver" && (
            <PrintHealthWidget venueId={venue?.id} compact />
          )}
          <button onClick={logout} className="text-xs text-[var(--ink-700)]" data-testid="mobile-logout">Logout</button>
        </div>
      </div>

      <main className="flex-1 min-w-0 mt-14 md:mt-0">
        <KioskReturnBanner />
        {showBanner && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-3 text-sm text-amber-900"
            data-testid="subscription-warning-banner"
          >
            <Warning size={16} weight="fill" className="text-amber-700 shrink-0" />
            <span className="flex-1 truncate">
              <span className="font-medium">{bannerLabel}</span>{" "}
              <span className="text-amber-800/80">Update billing to keep your venue running.</span>
            </span>
            <Link
              to="/settings?tab=billing"
              data-testid="subscription-warning-cta"
              className="shrink-0 h-8 px-3 rounded-md bg-amber-900 text-amber-50 text-xs font-medium inline-flex items-center hover:bg-amber-950"
            >
              Fix billing
            </Link>
          </div>
        )}
        <Outlet />
        {/* iter_130 (2026-06-13) — Online Order Popup Watcher. Mounted
            once inside Layout so the popup fires on any authenticated
            staff page. Skipped for delivery_driver role internally. */}
        {user && <OnlineOrderPopupWatcher venueId={venue?.id} role={role} />}
        {user && <KioskStaffAlerts role={role} />}
        {user && <HelpAlerts role={role} />}
        {/* Mobile bottom nav — role-aware. Delivery drivers see ONLY Delivery. */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[rgba(28,25,23,0.08)] z-40" data-testid="mob-nav">
          {role === "delivery_driver" ? (
            <div className="grid grid-cols-1">
              <NavLink to="/delivery" end data-testid="mob-nav-delivery"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 text-[10px] ${
                    isActive ? "text-[var(--terracotta)]" : "text-[var(--ink-700)]"
                  }`}
              >
                <Truck size={18} weight="regular" />
                <span className="mt-0.5">Delivery</span>
              </NavLink>
            </div>
          ) : (
            <div className="grid grid-cols-5">
              {[
                { to: "/", label: "Home", icon: House },
                { to: "/pos", label: "POS", icon: ShoppingCart },
                { to: "/kds", label: "KDS", icon: MonitorPlay },
                { to: "/orders", label: "Orders", icon: Receipt },
                { to: "/menu", label: "Menu", icon: ForkKnife },
              ].map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mob-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-2 text-[10px] ${
                      isActive ? "text-[var(--terracotta)]" : "text-[var(--ink-700)]"
                    }`
                  }
                >
                  <n.icon size={18} weight="regular" />
                  <span className="mt-0.5">{n.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
        <div className="md:hidden h-14" />
      </main>
    </div>
  );
}
